import { Text, Tooltip } from '@lmn/design-system';
import { JourneyContractSegmentTransportType, type SlimTransportTimeAndLocationProps } from '@lmn/lmn-components';
import React from 'react';
import styled, { css } from 'styled-components';
import InfoIcon from '@/app/components/search/common/Icons/InfoIcon';
import { IconGlyph } from '@/app/common/icons/MaskIcon';
import { useTranslation } from '@/app/components/translations/Translation';

export type EvoFullTransportTimeAndLocationProps = SlimTransportTimeAndLocationProps;

export const EvoFullTransportTimeAndLocation: React.FC<EvoFullTransportTimeAndLocationProps> = (props) => {
  const { time, differentDate, location, isArrival } = props;
  const { t } = useTranslation();
  // EvoTransport already sets transportType per leg, so no need to reach for the URL params here.
  const isTrain = location.transportType === JourneyContractSegmentTransportType.TRAIN;

  const locationContent = (
    <LocationWrapper $isArrival={isArrival}>
      <Location
        $highlight={location.highlight}
        data-testid={`transportcard-location-${isArrival ? 'arrival' : 'departure'}`}
      >
        {location.iataCode}
      </Location>
      {location.highlight && (
        <InfoIconWrapper>
          <InfoIcon />
        </InfoIconWrapper>
      )}
    </LocationWrapper>
  );

  return (
    <TimeAndLocationWrapper>
      <TimeAndDateWrapper $isArrival={isArrival}>
        <Time>{time}</Time>
        {differentDate && <DifferentDate>+{differentDate.plusDays}</DifferentDate>}
      </TimeAndDateWrapper>
      {location.highlight ? (
        <Tooltip
          content={t(
            isTrain
              ? 'vacation.oss.change_transport.different_station_tooltip'
              : 'vacation.oss.change_transport.different_airport_tooltip'
          )}
          placement="top"
        >
          {locationContent}
        </Tooltip>
      ) : (
        locationContent
      )}
    </TimeAndLocationWrapper>
  );
};

const TimeAndLocationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  white-space: nowrap;
`;

const TimeAndDateWrapper = styled.div<{ $isArrival: boolean }>`
  position: relative;
  display: flex;
  align-items: baseline;
  gap: 2px;
  ${({ $isArrival }) =>
    $isArrival &&
    css`
      justify-content: flex-end;
    `};
`;

const Time = styled(Text).attrs({ variant: 'Title2' })`
  line-height: 28px;
  font-size: 18px;
  font-weight: bold;
`;

const DifferentDate = styled(Text).attrs({ variant: 'BodySmall' })`
  color: ${({ theme }) => theme.kallisto.color.text.static.textWarning};
  font-weight: bold;
`;

const LocationWrapper = styled.div<{ $isArrival: boolean }>`
  display: flex;
  align-items: center;
  white-space: nowrap;
  gap: 4px;

  ${({ $isArrival }) =>
    $isArrival &&
    css`
      flex-direction: row-reverse;
    `};
`;

const Location = styled(Text).attrs({ variant: 'Body' })<{ $highlight: boolean }>`
  color: ${({ $highlight, theme }) =>
    $highlight ? theme.kallisto.color.text.static.textWarning : theme.kallisto.color.text.static.textSecondary};
  ${({ $highlight }) =>
    $highlight &&
    css`
      vertical-align: middle;
      text-decoration: underline;
      text-decoration-style: dotted;
      text-underline-offset: 4px;
      text-decoration-thickness: 13%;
      text-decoration-skip-ink: auto;
    `}
`;

const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.kallisto.color.icon.static.iconWarning};
  cursor: default;
  flex-shrink: 0;

  & > ${IconGlyph} {
    width: 16px;
    height: 16px;
  }
`;
